import {List, Map} from 'immutable';
import searchResultToCallDetails from 'utils/searchResultToCallDetails';
import User from 'records/User';
import mapValues from 'lodash/mapValues';
import stripHtml from 'utils/stripHtml';
import {Data, Int, RecordProps} from '@store/@types/util';
import CallDetails from 'records/CallDetails';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {FileUploadState} from 'records/FileUploadState';

interface FieldInterface {
  max: unknown;
  min: unknown;
  multiselect: string;
  name: string;
  options: List<FieldOption>; // [...FieldOption...]
  suggestions: List<unknown>; // [...StringFieldSuggestion...]
  order: Int;
  type: string;
  unit: unknown; // Specified only for numeric search fields
  unit_fix_position: unknown; // Specified only for numeric search fields
  value: string; // Specify how this field is filtered by the user
  group_id: unknown;
  group_name: unknown;
}

/**
 * Field schema. Defines everything the UI needs to render a field widget
 */
export class Field extends Data<FieldInterface>({
  max: null,
  min: null,
  multiselect: '',
  name: '',
  options: List(), // [...FieldOption...]
  suggestions: List(), // [...StringFieldSuggestion...]
  order: 0,
  type: '',
  unit: null, // Specified only for numeric search fields
  unit_fix_position: null, // Specified only for numeric search fields
  value: null, // Specify how this field is filtered by the user
  group_id: null,
  group_name: null,
}) {
  constructor(object: RecordProps<FieldInterface> = {}) {
    object = Object.assign({}, object);
    if (object.options != null) {
      object.options = List(object.options.map(item => new FieldOption(item)));
    }
    if (object.suggestions != null) {
      object.suggestions = List(object.suggestions);
    }
    super(object);
  }
}

interface FieldOptionInterface {
  attributes: List<unknown>;
  count: Int;
  display: string;
  icon: unknown;
  outputName: string;
}

/**
 * Field option for enum fields
 */
export class FieldOption extends Data<FieldOptionInterface>({
  attributes: List(),
  count: 0,
  display: '',
  icon: null,
  outputName: '',
}) {
  constructor(object: RecordProps<FieldOptionInterface> & {output_name?: string} = {}) {
    object = Object.assign({}, object);
    if (object.output_name !== undefined) {
      object.outputName = object.output_name;
      delete object.output_name;
    }
    super(object);
  }
}

interface SearchResultInterface {
  fields: List<unknown>; // [... DisplayField ...]
  id: FileUploadId;
  rank: string;
  caller: User; // info for the user who made the call
  values: Map<unknown, unknown>; // fields values by name for convenience
  share: Map<unknown, unknown>; // info for the FileUploadShare object attached to this FileUpload
  custom_content: Map<unknown, unknown>; // custom content for this FileUpload
  number_of_scorecard_results: Map<unknown, unknown>; // number of scorecard results for this FileUpload
  number_of_caller_scorecard_results: Map<unknown, unknown>; // number of scorecard results for this FileUpload by the caller
  number_of_votes_up: Map<unknown, unknown>; // number of votes up for this FileUpload
  number_of_votes_down: Map<unknown, unknown>; // number of votes down for this FileUpload
  state: FileUploadState; // Current state of this call in the pipeline

  // for holding a reference to newer CallDetails format for components that
  // use it
  callDetails: CallDetails | undefined;
}

/**
 * Individual search result. Everything the UI needs to render the metadata
 * for a call object
 */
export class SearchResult extends Data<SearchResultInterface>({
  fields: List(), // [... DisplayField ...]
  id: '',
  rank: '',
  caller: undefined, // info for the user who made the call
  values: Map(), // fields values by name for convenience
  share: Map(), // info for the FileUploadShare object attached to this FileUpload
  custom_content: Map(), // custom content for this FileUpload
  number_of_scorecard_results: Map(), // number of scorecard results for this FileUpload
  number_of_caller_scorecard_results: Map(), // number of scorecard results for this FileUpload by the caller
  number_of_votes_up: Map(), // number of votes up for this FileUpload
  number_of_votes_down: Map(), // number of votes down for this FileUpload
  state: undefined,

  // for holding a reference to newer CallDetails format for components that
  // use it
  callDetails: undefined,
}) {
  constructor(object: RecordProps<SearchResult> = {}) {
    super({
      ...object,
      caller: new User(object.caller),
      callDetails: searchResultToCallDetails(object),
      custom_content: mapValues(object.custom_content, v => stripHtml(v)),
    });
  }

  isComplete() {
    return this.state?.toLowerCase() === FileUploadState.COMPLETE;
  }
}

interface SearchResultsInterface {
  fields: List<Field>;
  results: List<SearchResult>;
}

/**
 * Search result object. Holds field definitions (for rendering sidebar) and
 * results (short version of call object)
 */
export class SearchResults extends Data<SearchResultsInterface>({
  fields: List(), // [... Field ...]
  results: List(), // [... SearchResult ...]
}) {
  constructor(object: RecordProps<SearchResults> = {}) {
    object = Object.assign({}, object);
    if (object.fields != null) {
      object.fields = List(
        object.fields.map(item => {
          const field = new Field(item);
          return field;
        })
      );
    }
    if (object.results != null) {
      object.results = List(object.results.map(item => new SearchResult(item)));
    }
    super(object);
  }
}
